<template>
  <div class="content-pack-sections">
    <v-expansion-panels v-model="openSections" multiple flat accordion class="section-panels">
      <v-expansion-panel v-for="section in sections" :key="section.key" class="section-panel">
        <v-expansion-panel-header class="section-header">
          <div class="section-bar">
            <div class="section-title-wrap">
              <svg
                v-if="section.svgIcon"
                class="content-pack-svg-icon section-title-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path v-for="path in section.svgIcon.paths" :key="path" :d="path" />
                <circle
                  v-for="circle in section.svgIcon.circles"
                  :key="`${circle.cx}-${circle.cy}-${circle.r}`"
                  :cx="circle.cx"
                  :cy="circle.cy"
                  :r="circle.r"
                />
              </svg>
              <v-icon v-else class="section-title-icon">
                {{ section.icon }}
              </v-icon>
              <div class="section-title font-weight-bold">
                {{ section.label }}
              </div>
            </div>
            <div class="section-actions" @click.stop>
              <v-btn icon small :title="`Expand all ${section.label}`" @click.stop="expandAllSubsections(section.key)">
                <v-icon>{{ icons.mdiChevronDoubleDown }}</v-icon>
              </v-btn>
              <v-btn icon small :title="`Collapse all ${section.label}`" @click.stop="collapseAllSubsections(section.key)">
                <v-icon>{{ icons.mdiChevronDoubleUp }}</v-icon>
              </v-btn>
            </div>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-expansion-panels v-model="openSubsections[section.key]" multiple flat class="subsection-panels">
            <v-expansion-panel v-for="subsection in section.items" :key="subsection.key">
              <v-expansion-panel-header class="subsection-header">
                <div class="subsection-bar">
                  <div class="subsection-title-wrap">
                    <svg
                      v-if="subsection.svgIcon"
                      class="content-pack-svg-icon subsection-title-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path v-for="path in subsection.svgIcon.paths" :key="path" :d="path" />
                      <circle
                        v-for="circle in subsection.svgIcon.circles"
                        :key="`${circle.cx}-${circle.cy}-${circle.r}`"
                        :cx="circle.cx"
                        :cy="circle.cy"
                        :r="circle.r"
                      />
                    </svg>
                    <v-icon v-else class="subsection-title-icon">
                      {{ subsection.icon }}
                    </v-icon>
                    <span class="subsection-title">{{ subsection.label }}</span>
                    <span class="subsection-count">{{ subsection.entries.length }}</span>
                  </div>
                  <div class="subsection-actions">
                    <v-icon small>
                      {{ icons.mdiChevronDown }}
                    </v-icon>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="subsection.entries.length === 0" class="text-caption grey--text font-italic py-2">
                  No items yet.
                </div>

                <v-list v-else dense class="py-0 subsection-list">
                  <v-list-item v-for="entry in subsection.entries" :key="entry.id" class="subsection-list-item">
                    <v-list-item-content>
                      <v-list-item-title>{{ entry.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        text
                        small
                        class="content-visibility-toggle"
                        @click.stop="toggleContentVisibility(section.key, subsection.key, entry)"
                      >
                        <v-icon
                          small
                          class="me-1"
                          :color="getContentVisibilityConfig(entry).color"
                        >
                          {{ getContentVisibilityConfig(entry).icon }}
                        </v-icon>
                        <span>{{ getContentVisibilityConfig(entry).label }}</span>
                      </v-btn>
                    </v-list-item-action>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        :disabled="isLoading"
                        :loading="isSaving"
                        @click="showDeleteDialog(section.key, subsection.key, entry)"
                      >
                        <v-icon>
                          {{ icons.mdiDeleteOutline }}
                        </v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel class="section-panel">
        <v-expansion-panel-header class="section-header">
          <div class="section-bar">
            <div class="section-title-wrap">
              <v-icon class="section-title-icon">
                {{ icons.mdiClipboardTextOutline }}
              </v-icon>
              <div class="section-title font-weight-bold">
                Training Plans
              </div>
            </div>
            <div class="section-actions" @click.stop>
              <div class="subsection-actions">
                <v-icon small>
                  {{ icons.mdiChevronDown }}
                </v-icon>
              </div>
            </div>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div v-if="planEntries.length === 0" class="text-caption grey--text font-italic py-2 plan-list-empty">
            No items yet.
          </div>

          <v-list v-else dense class="py-0 subsection-list">
            <v-list-item v-for="entry in planEntries" :key="entry.id" class="subsection-list-item">
              <v-list-item-content>
                <v-list-item-title>{{ entry.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  text
                  small
                  class="content-visibility-toggle"
                  :loading="isSaving"
                  @click.stop="toggleContentVisibility('plans', null, entry)"
                >
                  <v-icon
                    small
                    class="me-1"
                    :color="getContentVisibilityConfig(entry).color"
                  >
                    {{ getContentVisibilityConfig(entry).icon }}
                  </v-icon>
                  <span>{{ getContentVisibilityConfig(entry).label }}</span>
                </v-btn>
              </v-list-item-action>
              <v-list-item-action>
                <v-btn
                  icon
                  small
                  :disabled="isLoading"
                  :loading="isSaving"
                  @click="showDeleteDialog('plans', null, entry)"
                >
                  <v-icon>
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="isDeleteDialogVisible" max-width="640">
      <v-card>
        <v-card-title>Remove Item from Content Pack</v-card-title>
        <v-card-text>
          <p>
            Are you sure you want to remove <strong>{{ pendingDeleteEntry?.name }}</strong>
            from the content pack <strong>{{ item.displayName }}</strong>?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="hideDeleteDialog">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="isSaving" @click="confirmDelete">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiAvTimer,
  mdiClipboardTextOutline,
  mdiChevronDown,
  mdiChevronDoubleDown,
  mdiChevronDoubleUp,
  mdiDeleteOutline,
  mdiDumbbell,
  mdiFileChartOutline,
  mdiGolf,
  mdiGolfTee,
  mdiLockOutline,
  mdiLockOpenVariantOutline,
  mdiWeightLifter,
} from '@mdi/js'
import { computed, onMounted, ref } from 'vue'
import useAuth from '@/composables/useAuth'
import useContentPacks from '@/composables/api/contentPacks'
import useTrainingPlans from '@/composables/api/trainingPlans'

const normalizeReference = reference => {
  if (reference && typeof reference === 'object') {
    return {
      id: reference.id,
      academyId: reference.academyId ?? null,
    }
  }

  return {
    id: reference,
    academyId: null,
  }
}

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { getValidToken } = useAuth()
    const {
      trainingPlans,
      fetchAllTrainingPlans,
      updateTrainingPlanCompany,
    } = useTrainingPlans()
    const { contentPacks, fetchContentPacks } = useContentPacks()
    const exercises = ref([])
    const sessions = ref([])
    const sessionAssessments = ref([])

    const isSaving = ref(false)
    const isDeleteDialogVisible = ref(false)
    const pendingDeleteEntry = ref(null)
    const pendingDeleteSection = ref(null)
    const pendingDeleteSubsection = ref(null)
    const openSections = ref([0, 1, 2, 3])
    const openSubsections = ref({
      fitness: [0, 1, 2],
      practice: [0, 1, 2],
      flow: [0, 1, 2],
    })

    const theFlowIcons = {
      exercises: {
        paths: [
          'M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z',
          'M15 8v1M15 15v1M11 12h1M18 12h1',
        ],
        circles: [{ cx: 15, cy: 12, r: 3 }],
      },
      sessions: {
        paths: [
          'M3 20c0-2.5 2-4.5 4.5-4.5h.5',
          'M21 20c0-2.5-2-4.5-4.5-4.5h-.5',
          'M10 10q2-3 4 0',
        ],
        circles: [
          { cx: 6, cy: 10, r: 2 },
          { cx: 18, cy: 10, r: 2 },
        ],
      },
      assessments: {
        paths: [
          'M16 17a4 4 0 0 1-4 4 6 6 0 0 1-6-6 7 7 0 0 1 4-6.3V6a3 3 0 0 1 6 0v1.7a7 7 0 0 1 3 5.3',
          'M9 13l2 2 4-4',
        ],
        circles: [],
      },
    }

    const exerciseNameById = computed(() => (exercises.value || []).reduce((result, exercise) => {
      result[exercise.id] = exercise.name

      return result
    }, {}))

    const sessionNameById = computed(() => (sessions.value || []).reduce((result, session) => {
      result[session.id] = session.name

      return result
    }, {}))

    const assessmentNameById = computed(() => (sessionAssessments.value || []).reduce((result, assessment) => {
      result[assessment.id] = assessment.name

      return result
    }, {}))

    const trainingPlanById = computed(() => (trainingPlans.value || []).reduce((result, trainingPlan) => {
      result[trainingPlan.id] = trainingPlan

      return result
    }, {}))

    const requestFullContent = async path => {
      const token = await getValidToken()
      const baseUrl = process.env.VUE_APP_API_BASE_URL || ''
      const response = await fetch(`${baseUrl}${path}`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch ${path}: ${response.status}`)
      }

      return response.json()
    }

    const resolveEntries = (references, namesById) => references.map(reference => {
      const normalizedReference = normalizeReference(reference)

      return {
        id: normalizedReference.id,
        academyId: normalizedReference.academyId,
        name: namesById[normalizedReference.id] || normalizedReference.id,
      }
    })

    const planEntries = computed(() => (props.item.plans || []).map(planId => {
      const id = planId && typeof planId === 'object' ? planId.id : planId
      const trainingPlan = trainingPlanById.value[id]

      return {
        id,
        academyId: trainingPlan?.companyId ?? null,
        name: trainingPlan?.title || id,
      }
    }))

    const sections = computed(() => ([
      {
        key: 'fitness',
        label: 'Fitness',
        icon: mdiWeightLifter,
        items: [
          {
            key: 'exercises', label: 'Fitness Exercises', icon: mdiWeightLifter, entries: resolveEntries(props.item.fitness?.exercises || [], exerciseNameById.value),
          },
          {
            key: 'sessions', label: 'Fitness Routines', icon: mdiAvTimer, entries: resolveEntries(props.item.fitness?.sessions || [], sessionNameById.value),
          },
          {
            key: 'assessments', label: 'Fitness Assessments', icon: mdiDumbbell, entries: resolveEntries(props.item.fitness?.assessments || [], assessmentNameById.value),
          },
        ],
      },
      {
        key: 'practice',
        label: 'Practice',
        icon: mdiGolf,
        items: [
          {
            key: 'exercises', label: 'Practice Drills', icon: mdiGolf, entries: resolveEntries(props.item.practice?.exercises || [], exerciseNameById.value),
          },
          {
            key: 'sessions', label: 'Practice Sessions', icon: mdiFileChartOutline, entries: resolveEntries(props.item.practice?.sessions || [], sessionNameById.value),
          },
          {
            key: 'assessments', label: 'Golf Skills Assessments', icon: mdiGolfTee, entries: resolveEntries(props.item.practice?.assessments || [], assessmentNameById.value),
          },
        ],
      },
      {
        key: 'flow',
        label: 'The Flow',
        svgIcon: theFlowIcons.exercises,
        items: [
          {
            key: 'exercises', label: 'The Flow Exercises', svgIcon: theFlowIcons.exercises, entries: resolveEntries(props.item.flow?.exercises || [], exerciseNameById.value),
          },
          {
            key: 'sessions', label: 'The Flow Sessions', svgIcon: theFlowIcons.sessions, entries: resolveEntries(props.item.flow?.sessions || [], sessionNameById.value),
          },
          {
            key: 'assessments', label: 'The Flow Assessments', svgIcon: theFlowIcons.assessments, entries: resolveEntries(props.item.flow?.assessments || [], assessmentNameById.value),
          },
        ],
      },
    ]))

    onMounted(async () => {
      const [allExercises, allSessions, allAssessments] = await Promise.all([
        requestFullContent('/exercise?withCreator=true&allAcademies=true'),
        requestFullContent('/session?withCreator=true&allAcademies=true'),
        requestFullContent('/assessment/group?withCreator=true&allAcademies=true'),
      ])

      exercises.value = allExercises || []
      sessions.value = allSessions || []
      sessionAssessments.value = allAssessments || []

      await Promise.all([fetchAllTrainingPlans(), fetchContentPacks()])
    })

    const expandAllSubsections = sectionKey => {
      openSubsections.value = {
        ...openSubsections.value,
        [sectionKey]: [0, 1, 2],
      }
    }

    const collapseAllSubsections = sectionKey => {
      openSubsections.value = {
        ...openSubsections.value,
        [sectionKey]: [],
      }
    }

    const getContentVisibilityConfig = entry => {
      const isContentPacksOnly = entry.academyId === 'CONTENTPACK'

      return isContentPacksOnly
        ? { label: 'Content Packs Only', icon: mdiLockOutline, color: 'error' }
        : { label: 'Global Content & CP', icon: mdiLockOpenVariantOutline, color: 'success darken-3' }
    }

    const toggleContentVisibility = async (sectionKey, subsectionKey, entry) => {
      if (sectionKey === 'plans') {
        isSaving.value = true
        try {
          const companyId = entry.academyId === 'CONTENTPACK' ? null : 'CONTENTPACK'
          await updateTrainingPlanCompany([entry.id], companyId)
          await fetchAllTrainingPlans()
        } finally {
          isSaving.value = false
        }

        return
      }

      const updatedItem = structuredClone(props.item)
      const references = updatedItem[sectionKey]?.[subsectionKey] || []

      updatedItem[sectionKey][subsectionKey] = references.map(reference => {
        const normalizedReference = normalizeReference(reference)

        if (normalizedReference.id !== entry.id) {
          return normalizedReference
        }

        return {
          ...normalizedReference,
          academyId: normalizedReference.academyId === 'CONTENTPACK' ? null : 'CONTENTPACK',
        }
      })

      emit('update:item', updatedItem)
    }

    const removeReference = (references, entryId) => references
      .map(reference => normalizeReference(reference))
      .filter(reference => reference.id !== entryId)

    const ensurePlanIds = plans => plans.map(plan => (
      plan && typeof plan === 'object' ? plan.id : plan
    ))

    const isPlanInAnotherContentPack = planId => contentPacks.value.some(contentPack => {
      if (contentPack.id === props.item.id) return false

      return (contentPack.plans || []).some(plan => (typeof plan === 'object' ? plan.id : plan) === planId)
    })

    const updateContentPackLists = updatedItem => {
      updatedItem.fitness = updatedItem.fitness || {}
      updatedItem.practice = updatedItem.practice || {}
      updatedItem.flow = updatedItem.flow || {}

      const sectionKeys = ['fitness', 'practice', 'flow']
      const subsectionKeys = ['exercises', 'sessions', 'assessments']
      sectionKeys.forEach(sectionKey => {
        subsectionKeys.forEach(subsectionKey => {
          updatedItem[sectionKey][subsectionKey] = (updatedItem[sectionKey][subsectionKey] || [])
            .map(reference => normalizeReference(reference))
        })
      })

      updatedItem.plans = ensurePlanIds(updatedItem.plans || [])

      return updatedItem
    }

    const emitUpdatedItem = updatedItem => {
      emit('update:item', updateContentPackLists(updatedItem))
    }

    const hideDeleteDialog = () => {
      isDeleteDialogVisible.value = false
      pendingDeleteEntry.value = null
      pendingDeleteSection.value = null
      pendingDeleteSubsection.value = null
    }

    const confirmDelete = async () => {
      if (!pendingDeleteEntry.value || !pendingDeleteSection.value) return

      isSaving.value = true
      try {
        const updatedItem = structuredClone(props.item)

        if (pendingDeleteSection.value === 'plans') {
          updatedItem.plans = ensurePlanIds(updatedItem.plans || []).filter(id => id !== pendingDeleteEntry.value.id)
          if (pendingDeleteEntry.value.academyId === 'CONTENTPACK' && !isPlanInAnotherContentPack(pendingDeleteEntry.value.id)) {
            await updateTrainingPlanCompany([pendingDeleteEntry.value.id], null)
            await fetchAllTrainingPlans()
          }
        } else {
          updatedItem[pendingDeleteSection.value][pendingDeleteSubsection.value] = removeReference(
            updatedItem[pendingDeleteSection.value][pendingDeleteSubsection.value] || [],
            pendingDeleteEntry.value.id,
          )
        }

        emitUpdatedItem(updatedItem)
        hideDeleteDialog()
      } finally {
        isSaving.value = false
      }
    }

    const showDeleteDialog = (sectionKey, subsectionKey, entry) => {
      pendingDeleteSection.value = sectionKey
      pendingDeleteSubsection.value = subsectionKey
      pendingDeleteEntry.value = entry
      isDeleteDialogVisible.value = true
    }

    return {
      sections,
      planEntries,
      openSections,
      openSubsections,
      expandAllSubsections,
      collapseAllSubsections,
      getContentVisibilityConfig,
      toggleContentVisibility,
      isDeleteDialogVisible,
      pendingDeleteEntry,
      isSaving,
      showDeleteDialog,
      hideDeleteDialog,
      confirmDelete,
      icons: {
        mdiChevronDown,
        mdiChevronDoubleDown,
        mdiChevronDoubleUp,
        mdiDeleteOutline,
        mdiClipboardTextOutline,
      },
    }
  },
}
</script>

<style scoped>
.content-pack-sections .w-100 {
  width: 100%;
}

.content-pack-sections .ga-2 {
  gap: 8px;
}

.section-bar,
.subsection-bar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 6px;
}

.section-bar {
  background: rgba(255, 255, 255, 0.06);
  padding: 6px 8px 6px 12px;
}

.subsection-bar {
  background: rgba(0, 0, 0, 0.11);
  padding: 4px 8px 4px 12px;
}

.section-panel {
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 0;
  margin-bottom: 12px;
}

.section-title,
.subsection-title,
.subsection-count {
  line-height: 24px;
}

.section-title-wrap,
.subsection-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title-icon,
.subsection-title-icon {
  flex: 0 0 auto;
}

.content-pack-svg-icon {
  display: block;
  flex: 0 0 auto;
  color: currentColor;
}

.section-title {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.section-actions,
.subsection-actions,
.subsection-title-wrap {
  display: flex;
  align-items: center;
}

.section-actions {
  justify-content: flex-end;
  margin-left: auto;
  gap: 4px;
}

.subsection-title-wrap {
  gap: 8px;
}

.subsection-count {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.subsection-actions {
  justify-content: flex-end;
  min-width: 32px;
  margin-left: auto;
  gap: 8px;
}

.content-visibility-toggle {
  min-width: 0;
  padding: 0 6px !important;
  letter-spacing: 0;
  text-transform: none;
}

.content-visibility-toggle ::v-deep(.v-btn__content) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.subsection-list {
  margin-left: 8ch;
}

.subsection-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  min-height: 36px;
}

.subsection-list-item:last-child {
  border-bottom: 0;
}

.plan-list-empty {
  margin-left: 8ch;
}

.content-pack-sections ::v-deep(.subsection-list-item.v-list-item) {
  padding-left: 6px;
  padding-right: 0;
}

.content-pack-sections ::v-deep(.subsection-list-item .v-list-item__content) {
  padding: 6px 0;
}

.content-pack-sections ::v-deep(.subsection-list-item .v-list-item__title) {
  font-weight: 400;
}

.content-pack-sections ::v-deep(.subsection-list-item .v-list-item__action) {
  margin: 0 0 0 8px;
}

.content-pack-sections ::v-deep(.section-header .v-expansion-panel-header__icon),
.content-pack-sections ::v-deep(.subsection-header .v-expansion-panel-header__icon) {
  display: none;
}

.content-pack-sections ::v-deep(.section-header.v-expansion-panel-header),
.content-pack-sections ::v-deep(.subsection-header.v-expansion-panel-header) {
  padding-right: 12px;
}

.content-pack-sections ::v-deep(.section-panel.v-expansion-panel) {
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: none;
}

.content-pack-sections ::v-deep(.section-panel > .v-expansion-panel-content > .v-expansion-panel-content__wrap) {
  padding-top: 2px;
  padding-bottom: 2px;
}

.content-pack-sections ::v-deep(.subsection-panels) {
  padding-top: 0;
}

.content-pack-sections ::v-deep(.subsection-panels .v-expansion-panel:first-child) {
  margin-top: 0;
}

.content-pack-sections ::v-deep(.subsection-panels .v-expansion-panel + .v-expansion-panel) {
  margin-top: 1px;
}

.content-pack-sections ::v-deep(.subsection-panels .v-expansion-panel-content__wrap) {
  padding-top: 2px;
  padding-bottom: 2px;
}

.content-pack-sections ::v-deep(.subsection-header.v-expansion-panel-header--active .subsection-actions .v-icon),
.content-pack-sections ::v-deep(.section-header.v-expansion-panel-header--active .subsection-actions .v-icon) {
  transform: rotate(180deg);
}
</style>
